import React, { FC, useCallback, useState } from "react";
import { OrderProcessInfoBox, OrderStepTrackerResponseItem } from "shared/api";
import { useFormikContext } from "formik";
import {
  useInputMaterialsProductionPageData,
  useOutputMaterialDataForProductionPage,
  useOutputMaterialsProductionPageData,
  useProdPageContentData,
} from "shared/hooks";
import { InputMaterialsContainer } from "./InputMaterialsContainer";
import { TrackingList } from "./TrackingList";
import { AddProductionRequest } from "./ProcessStepProduction";
import { OutputMaterialsContainer } from "./OutputMaterialsTable";
import { OrderCard } from "./OrderCard";

interface ProdPageContentProps {
  orderStepTrackerResponseItem?: OrderStepTrackerResponseItem;
  processStepId?: number;
  orderId?: string;
  openProducedItemInOrderModal: () => void;
  refetchOrderProcessStepTracker: () => void;
  orderProcessStepInfoBox?: OrderProcessInfoBox;
  showMaintenanceTable: boolean;
  handleshowMaintenanceIconState: (value: boolean) => void;
  locked: boolean;
  handleLocked: (value: boolean) => void;
  setShowMaintenanceTable: React.Dispatch<React.SetStateAction<boolean>>;
  statusModalOpen?: boolean;
  setStatusModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  tableRef: React.RefObject<HTMLDivElement>;
}

const ProdPageContent: FC<ProdPageContentProps> = ({
  orderStepTrackerResponseItem,
  processStepId,
  orderId,
  openProducedItemInOrderModal,
  refetchOrderProcessStepTracker,
  orderProcessStepInfoBox,
  showMaintenanceTable,
  handleshowMaintenanceIconState,
  locked,
  handleLocked,
  setShowMaintenanceTable,
  tableRef,
  setStatusModalOpen,
  statusModalOpen,
}) => {
  const { setFieldValue, values } = useFormikContext<AddProductionRequest>();

  const { inputMaterials, adaptedInitialInputMaterials } =
    useInputMaterialsProductionPageData({
      orderStepTrackerResponseItem,
    });

  const { adaptedMachines } = useProdPageContentData({
    orderId,
    orderStepTrackerResponseItem,
  });

  const { outputMaterials, adaptedInitialOutputMaterials } =
    useOutputMaterialsProductionPageData({
      orderStepTrackerResponseItem,
      setFieldValue,
    });

  const {
    handleCountOfItemsChange,
    isEditOutputMaterialsMode,
    toggleEditMode,
  } = useOutputMaterialDataForProductionPage({
    mainValues: values,
    orderStepTrackerResponseItem,
  });

  const [isUnlocked, setIsUnlocked] = useState<Record<number, boolean>>({});

  const toggleUnlockField = useCallback((inputIndex: number) => {
    setIsUnlocked((prevState) => ({
      ...prevState,
      [inputIndex]: !prevState[inputIndex],
    }));
  }, []);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isOrderCardMachineSelected, setIsOrderCardMachineSelected] =
    useState(false);
  const [statusSummary, setStatusSummary] = useState<{
    Prima: number;
    Waste: number;
    Investigation: number;
  }>({
    Prima: 0,
    Waste: 0,
    Investigation: 0,
  });

  return (
    <div className="min-h-screen bg-gray-50 py-2 px-5 w-screen">
      <div className="flex flex-col gap-5 w-full mb-8 items-start justify-center">
        <OrderCard
          tableRef={tableRef}
          statusModalOpen={statusModalOpen}
          setStatusModalOpen={setStatusModalOpen}
          setShowMaintenanceTable={setShowMaintenanceTable}
          refetchOrderProcessStepTracker={refetchOrderProcessStepTracker}
          processStepId={processStepId}
          orderStepTrackerResponseItem={orderStepTrackerResponseItem}
          adaptedMachines={adaptedMachines}
          openProducedItemInOrderModal={openProducedItemInOrderModal}
          setIsOrderCardMachineSelected={setIsOrderCardMachineSelected}
          isOrderCardMachineSelected={isOrderCardMachineSelected}
          isSubmitted={isSubmitted}
          showMaintenanceTable={showMaintenanceTable}
          handleshowMaintenanceIconState={handleshowMaintenanceIconState}
        />
        {inputMaterials && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleLocked(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleLocked(false);
              }
            }}
            className="relative w-full"
          >
            {locked && (
              <div className="absolute inset-0 z-10 bg-white bg-opacity-70 flex flex-col justify-center items-center pointer-events-none">
                <span className="text-red-600 font-bold text-lg">LOCKED</span>
                <span className="text-red-600 font-bold text-lg">
                  CLICK HERE TO <span className="text-green-700">ACTIVATE</span>
                </span>
              </div>
            )}

            <div className={!locked ? "" : "opacity-50 pointer-events-none"}>
              <InputMaterialsContainer
                isUnlocked={isUnlocked}
                toggleUnlockField={toggleUnlockField}
                orderStepTrackerResponseItem={orderStepTrackerResponseItem!}
                adaptedInitialInputMaterials={adaptedInitialInputMaterials}
              />
            </div>
          </div>
        )}

        <div className="flex justify-between w-full">
          {outputMaterials && (
            <div className="basis-[100%] p-2">
              <OutputMaterialsContainer
                handleLocked={handleLocked}
                locked={locked}
                statusSummary={statusSummary}
                setIsSubmitted={setIsSubmitted}
                isOrderCardMachineSelected={isOrderCardMachineSelected}
                processStepId={processStepId}
                orderProcessStepInfoBox={orderProcessStepInfoBox}
                orderStepTrackerResponseItem={orderStepTrackerResponseItem}
                handleCountOfItemsChange={handleCountOfItemsChange}
                isEditMode={isEditOutputMaterialsMode}
                toggleEditMode={toggleEditMode}
                setIsUnlocked={setIsUnlocked}
                outputMaterialsList={values?.output_materials}
                setFieldValue={setFieldValue}
                adaptedInitialInputMaterials={adaptedInitialInputMaterials}
                adaptedInitialOutputMaterials={adaptedInitialOutputMaterials}
                refetchOrderProcessStepTracker={refetchOrderProcessStepTracker}
              />
            </div>
          )}
        </div>
      </div>

      <TrackingList
        adaptedInitialInputMaterials={adaptedInitialInputMaterials}
        inputMaterials={inputMaterials || []}
        orderStepTrackerResponseItem={orderStepTrackerResponseItem}
        onStatusSummaryChange={setStatusSummary}
      />
    </div>
  );
};

export { ProdPageContent };
